import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core'
import { IBvUser } from '@modules/bv-api'
import { ActivityEventTypes, IBvNotification } from '@modules/bv-notifications'
import { Store } from '@ngrx/store'
import { MapboxMapCoordinator, NavigationService } from '@services'
import TranslationService from '@services/translation.service'
import { DatePipe } from '@angular/common'
import { FeatureAPIService, TaskAPIService } from '@ziptility/modules'
import { firstValueFrom } from 'rxjs'
import { ActivatedRoute } from '@angular/router'
import { Feature } from '@turf/turf'
import { IconsModule } from '@ziptility/icons'
import { MatDialogRef } from '@angular/material/dialog'
import { UserAvatarComponent } from '../components/user-avatar/user-avatar.component'
import { MarkdownComponent } from 'ngx-markdown'
import { MapSearchService } from '@ziptility/search/map-search.service'

@Component({
  selector: 'notification',
  template: `
    @let notification = this.notification();
    <div
      class="notification {{ notification.code }} py-2 cursor-pointer hover:bg-gray-200 px-2 rounded"
      (click)="onSelectNotification()"
    >
      <div class="flex flex-row">
        <user-avatar [firstName]="notification.creatorName" [displayStyle]="'initials'"></user-avatar>
        <div style="margin-left: 10px">
          <markdown>
            {{ notification.description }}
          </markdown>
          <div class="flex items-center space-x-3 date">
            <div>
              {{ notification.dateCreated | date: 'short' }}
            </div>
            @if (notification.featureId) {
              <div
                class="flex items-center space-x-1 cursor-pointer underline"
                (click)="$event.stopPropagation(); _goToFeature()"
              >
                <i-feather class="feather-icon-sm flex-shrink-0" name="map-pin"></i-feather>
                <div>
                  {{ featureIdentifier }}
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .notification {
        font-size: 15px;
        color: #0c1f30;
        position: relative;
        margin-bottom: 12px;
      }
      .date {
        font-size: 13px;
        color: #9da6ac;
        margin-top: 2px;
        opacity: 0.9;
        font-weight: 500;
        line-height: 1.33;
        letter-spacing: -0.2px;
        color: #a3a7aa;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, IconsModule, UserAvatarComponent, MarkdownComponent],
})
export class NotificationComponent {
  readonly ActivityEventTypes = ActivityEventTypes
  readonly store = inject(Store)
  readonly translationService = inject(TranslationService)
  readonly navigationService = inject(NavigationService)
  readonly featureService = inject(FeatureAPIService)
  readonly taskService = inject(TaskAPIService)
  readonly route = inject(ActivatedRoute)
  readonly mapCoordinator = inject(MapboxMapCoordinator)
  readonly matDialogRef = inject(MatDialogRef, { optional: true })
  readonly mapSearchService = inject(MapSearchService)

  readonly notification = input.required<IBvNotification>()
  readonly user = input<IBvUser>()
  readonly hideDestination = input<boolean>(false)

  get featureIdentifier(): string {
    const { dataInfo, featureId } = this.notification()
    const layerTitle = dataInfo?.layer?.title ?? ''
    return `${this.translationService.singularize(layerTitle)} ${dataInfo?.feature?.systemId ?? featureId}`
  }

  onSelectNotification() {
    this.matDialogRef?.close()
    const { taskId, productId, featureId } = this.notification()
    if (taskId) {
      this.goToTask()
      return
    }

    if (productId) {
      this.goToProduct()
      return
    }

    if (featureId) {
      this.goToFeature()
    }
  }

  goToMap() {
    const mapId = this.notification().mapId
    if (mapId) {
      this.navigationService.navigateToMap(mapId)
    }
  }

  _goToFeature() {
    this.matDialogRef?.close()
    this.goToFeature()
  }

  async goToFeature() {
    try {
      const featureId = this.notification().featureId
      if (!featureId) {
        return
      }

      const child = this.route.snapshot.firstChild
      const mapId = child?.paramMap.get('mapId')
      if (!mapId) {
        return
      }

      const feature = await firstValueFrom(this.featureService.getDetail(featureId))
      const geometry = feature.geometry
        ? { type: feature.geometry.type, coordinates: feature.geometry.coordinates }
        : null

      this.mapSearchService.searchResult.emit({
        type: 'asset',
        query: this.featureIdentifier,
        feature: {
          type: 'Feature',
          geometry,
          properties: {
            title: this.featureIdentifier,
            asset: {
              id: featureId,
              layerId: feature.layerId,
              systemId: `${feature.systemId}`,
            },
          },
        },
      } as Parameters<typeof this.mapSearchService.searchResult.emit>[0])
    } catch (error) {
      console.log(error)
    }
  }

  goToComment() {
    const featureId = this.notification().featureId
    if (!featureId) {
      return
    }
    this.navigationService.navigateToFeature(featureId, {
      queryParams: {
        commentId: this.notification().commentId,
      },
      queryParamsHandling: 'merge',
    })
  }

  async goToTask() {
    const taskId = this.notification().taskId
    if (!taskId) {
      return
    }
    try {
      const task = await firstValueFrom(this.taskService.get(taskId))
      if (!task.feature) {
        this.navigationService.navigateToMapTask(taskId)
        return
      }

      const features = [task.feature]
      const featureId = this.notification().featureId
      if (!featureId) {
        return
      }
      this.navigationService.navigateToFeatureTask(featureId, taskId, {
        queryParams: {
          bounds: this.mapCoordinator.getBoundsForFeatures(features as Feature[]),
        },
      })
    } catch (error) {
      console.log(error)
    }
  }

  goToProduct() {
    this.navigationService.navigateToInventoryDashboard({
      queryParams: {
        productId: this.notification().productId,
      },
    })
  }
}
